<template>
    <v-container fluid>
        <v-alert v-if="missingDataAlert" type="warning" variant="tonal" class="mb-4" closable>
            <div class="d-flex align-center justify-space-between">
                <span>{{ $t('lieferanten.rechnungformular.stammdatenInkomplett') }}</span>
                <v-btn color="warning" variant="elevated" size="small" @click="goToLieferanteinstellungen"
                    >{{ $t('lieferanten.rechnungformular.lieferanteneinstellungen') }}
                </v-btn>
            </div>
        </v-alert>
        <v-card class="mb-4">
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <h4 class="mb-3">{{ $t('lieferanten.rechnungformular.pdfUpload') }} *</h4>
                        <div class="d-flex align-center">
                            <v-file-input
                                v-model="pdfFile"
                                accept=".pdf,application/pdf"
                                :label="$t('lieferanten.rechnungformular.pdfAuswaehlen')"
                                variant="outlined"
                                density="comfortable"
                                prepend-icon="mdi-file-pdf-box"
                                show-size
                                clearable
                                class="flex-grow-1"
                                :error-messages="pdfFile ? [] : [$t('lieferanten.rechnungformular.pdfErforderlich')]"
                                @update:model-value="onPdfFileChanged"
                            ></v-file-input>
                            <v-btn
                                v-if="pdfPreviewUrl"
                                icon="mdi-eye"
                                variant="text"
                                size="small"
                                color="primary"
                                class="ml-2 mb-5"
                                :title="$t('lieferanten.rechnungformular.pdfVorschau')"
                                @click="openPdfPreview"
                            >
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <Form ref="formRef" v-slot="{ meta, validate }">
            <v-expansion-panels v-model="activePanel" variant="accordion">
                <!-- Section 1: Header -->
                <v-expansion-panel value="header" data-section="header" eager>
                    <v-expansion-panel-title>
                        <div class="d-flex align-center">
                            <h3>{{ $t('lieferanten.rechnungsformular.headerTitle') }}</h3>
                            <v-icon v-if="headerSectionValid" color="success" class="ml-2">mdi-check-circle</v-icon>
                            <v-icon v-else-if="headerSectionTouched" color="error" class="ml-2">mdi-alert-circle</v-icon>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.documentType"
                                    name="documentType"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.documentType')"
                                >
                                    <v-select
                                        v-model="formData.documentType"
                                        :items="dokumentTypes"
                                        :label="$t('lieferanten.rechnungformular.dokumenttyp') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-select>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.documentIdentifier"
                                    name="documentIdentifier"
                                    :label="$t('lieferanten.rechnungformular.rechnungsnummer')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.documentIdentifier')"
                                >
                                    <v-text-field
                                        v-model="formData.documentIdentifier"
                                        :label="$t('lieferanten.rechnungformular.rechnungsnummer') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        type="text"
                                        inputmode="numeric"
                                        maxlength="12"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                        @input="formData.documentIdentifier = ($event.target as HTMLInputElement).value.replace(/\D/g, '')"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.documentIssueDate"
                                    name="documentIssueDate"
                                    :label="$t('lieferanten.rechnungformular.rechnungsdatum')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.documentIssueDate')"
                                >
                                    <NowhowDatepicker
                                        v-model="formData.documentIssueDate"
                                        :label="$t('lieferanten.rechnungformular.rechnungsdatum') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        :error="errors.length > 0"
                                        @blur="handleBlur"
                                    ></NowhowDatepicker>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.customerNumber"
                                    name="customerNumber"
                                    :label="$t('lieferanten.rechnungformular.kundennummerBeimLieferanten')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.customerNumber')"
                                >
                                    <v-text-field
                                        v-model="formData.customerNumber"
                                        :label="$t('lieferanten.rechnungformular.kundennummerBeimLieferanten') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.deliveryDate"
                                    name="deliveryDate"
                                    :label="$t('lieferanten.rechnungformular.lieferdatum')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.deliveryDate')"
                                >
                                    <NowhowDatepicker
                                        v-model="formData.deliveryDate"
                                        :label="$t('lieferanten.rechnungformular.lieferdatum') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></NowhowDatepicker>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field v-slot="{ errors, handleBlur }" v-model="formData.orderDate" name="orderDate">
                                    <NowhowDatepicker
                                        v-model="formData.orderDate"
                                        :label="$t('lieferanten.rechnungformular.bestelldatum')"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></NowhowDatepicker>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field v-slot="{ errors, handleBlur }" v-model="formData.orderReference" name="orderReference">
                                    <v-text-field
                                        v-model="formData.orderReference"
                                        :label="$t('lieferanten.rechnungformular.bestellreferenz')"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                        @keydown.tab="handleTabFromLastField($event, 'header')"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="text-right">
                                <v-btn color="primary" tabindex="-1" @click="goToNext('customer')">
                                    {{ $t('common.next') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>

                <!-- Section 2: Customer -->
                <v-expansion-panel value="customer" data-section="customer" eager>
                    <v-expansion-panel-title>
                        <div class="d-flex align-center">
                            <h3>{{ $t('lieferanten.rechnungformular.kunde') }}</h3>
                            <v-icon v-if="customerSectionValid" color="success" class="ml-2">mdi-check-circle</v-icon>
                            <v-icon v-else-if="customerSectionTouched" color="error" class="ml-2">mdi-alert-circle</v-icon>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.tvsCustomerNumber"
                                    name="tvsCustomerNumber"
                                    :label="$t('lieferanten.rechnungformular.tvskundennummer')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.tvsCustomerNumber')"
                                >
                                    <v-text-field
                                        v-model="formData.tvsCustomerNumber"
                                        :label="$t('lieferanten.rechnungformular.tvskundennummer') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        maxlength="4"
                                        type="text"
                                        inputmode="numeric"
                                        :error-messages="errors"
                                        append-inner-icon="mdi-magnify"
                                        @blur="handleBlur"
                                        @click:append-inner="showCustomerPicker = true"
                                        @input="formData.tvsCustomerNumber = ($event.target as HTMLInputElement).value.replace(/\D/g, '')"
                                        @keydown.tab="handleTabToPositions($event)"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.companyName"
                                    name="companyName"
                                    :label="$t('lieferanten.rechnungformular.firmenname')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.companyName')"
                                >
                                    <v-text-field
                                        v-model="formData.companyName"
                                        :label="$t('lieferanten.rechnungformular.firmenname') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.address1"
                                    name="address1"
                                    :label="$t('lieferanten.rechnungformular.adresse1')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.address1')"
                                >
                                    <v-text-field
                                        v-model="formData.address1"
                                        :label="$t('lieferanten.rechnungformular.adresse1') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.address2"
                                    name="address2"
                                    :label="$t('lieferanten.rechnungformular.adresse2')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.address2')"
                                >
                                    <v-text-field
                                        v-model="formData.address2"
                                        :label="$t('lieferanten.rechnungformular.adresse2')"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.address3"
                                    name="address3"
                                    :label="$t('lieferanten.rechnungformular.adresse3')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.address3')"
                                >
                                    <v-text-field
                                        v-model="formData.address3"
                                        :label="$t('lieferanten.rechnungformular.adresse3')"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.zipCode"
                                    name="zipCode"
                                    :label="$t('lieferanten.rechnungformular.plz')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.zipCode')"
                                >
                                    <v-text-field
                                        v-model="formData.zipCode"
                                        :label="$t('lieferanten.rechnungformular.plz') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        type="text"
                                        inputmode="numeric"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                        @input="formData.zipCode = ($event.target as HTMLInputElement).value.replace(/\D/g, '')"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.cityName"
                                    name="cityName"
                                    :label="$t('lieferanten.rechnungformular.ort')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.cityName')"
                                >
                                    <v-text-field
                                        v-model="formData.cityName"
                                        :label="$t('lieferanten.rechnungformular.ort') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <Field
                                    v-slot="{ errors, handleBlur }"
                                    v-model="formData.country"
                                    name="country"
                                    :label="$t('lieferanten.rechnungformular.land')"
                                    :rules="getValidation('lieferantRechnungFormularApiDto.country')"
                                >
                                    <v-text-field
                                        v-model="formData.country"
                                        :label="$t('lieferanten.rechnungformular.land') + ' *'"
                                        variant="outlined"
                                        density="comfortable"
                                        maxlength="2"
                                        :error-messages="errors"
                                        @blur="handleBlur"
                                        @keydown.tab="handleTabFromLastField($event, 'customer')"
                                    ></v-text-field>
                                </Field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="text-right">
                                <v-btn color="primary" tabindex="-1" @click="goToNext('positions')">
                                    {{ $t('common.next') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
                <!-- Section 3: Positions -->
                <v-expansion-panel value="positions" data-section="positions" eager>
                    <v-expansion-panel-title>
                        <div class="d-flex align-center">
                            <h3>{{ $t('lieferanten.rechnungformular.positionen') }}</h3>
                            <v-chip class="ml-2" size="small" color="primary">{{ positions.length }}</v-chip>
                        </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-btn color="primary" class="mb-4" @click="addPosition(false)">
                            <v-icon left>mdi-plus</v-icon>
                            {{ $t('lieferanten.rechnungformular.positionHinzufuegen') }}
                        </v-btn>
                        <v-expansion-panels v-model="expandedPositions" multiple>
                            <v-expansion-panel v-for="(position, index) in positions" :key="getPositionId(position)" :value="getPositionId(position)" eager>
                                <v-expansion-panel-title>
                                    <div class="d-flex align-center justify-space-between w-100">
                                        <span>{{ $t('lieferanten.rechnungformular.position') }} {{ index + 1 }}</span>
                                        <v-btn color="error" size="small" icon @click.stop="removePosition(index)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                    <template #actions>
                                        <v-icon>mdi-chevron-down</v-icon>
                                    </template>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.SupplierArticleNumber"
                                                :name="`positions[${getPositionId(position)}].SupplierArticleNumber`"
                                                :label="$t('lieferanten.rechnungformular.lieferantenProduktnummer')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.supplierArticleNumber')"
                                            >
                                                <v-text-field
                                                    v-model="position.SupplierArticleNumber"
                                                    :label="$t('lieferanten.rechnungformular.lieferantenProduktnummer') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @blur="
                                                        () => {
                                                            handleBlur();
                                                            onSupplierArticleNumberChange(index);
                                                        }
                                                    "
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.VetpointArticleNumber"
                                                :label="$t('lieferanten.rechnungformular.vetpointProduktnummer')"
                                                :name="`positions[${getPositionId(position)}].VetpointArticleNumber`"
                                            >
                                                <v-text-field
                                                    v-model="position.VetpointArticleNumber"
                                                    :label="$t('lieferanten.rechnungformular.vetpointProduktnummer')"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :hint="$t('lieferanten.rechnungformular.autoFillHint')"
                                                    persistent-hint
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <Field v-slot="{ errors, handleBlur }" v-model="position.gtin" :name="`positions[${getPositionId(position)}].gtin`">
                                                <v-text-field
                                                    v-model="position.gtin"
                                                    label="GTIN"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.productName"
                                                :name="`positions[${getPositionId(position)}].productName`"
                                                :label="$t('lieferanten.rechnungformular.produktname')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.description')"
                                            >
                                                <v-text-field
                                                    v-model="position.productName"
                                                    :label="$t('lieferanten.rechnungformular.produktname') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="3">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.quantity"
                                                :name="`positions[${getPositionId(position)}].quantity`"
                                                :label="$t('lieferanten.rechnungformular.menge')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.quantity')"
                                            >
                                                <v-text-field
                                                    v-model.number="position.quantity"
                                                    :label="$t('lieferanten.rechnungformular.menge') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    type="number"
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                    @input="calculatePositionTotals(index)"
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.unitOfMeasurement"
                                                :name="`positions[${getPositionId(position)}].unitOfMeasurement`"
                                                :label="$t('lieferanten.rechnungformular.mengeneinheit')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.unitOfMeasurement')"
                                            >
                                                <v-select
                                                    v-model="position.unitOfMeasurement"
                                                    :items="unitOfMeasurementItems"
                                                    :label="$t('lieferanten.rechnungformular.mengeneinheit') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                ></v-select>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.unitPrice"
                                                :name="`positions[${getPositionId(position)}].unitPrice`"
                                                :label="$t('lieferanten.rechnungformular.einzelpreis')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.unitPrice')"
                                            >
                                                <v-text-field
                                                    :model-value="getCurrencyDisplayValue(`pos_${getPositionId(position)}_unitPrice`, position.unitPrice)"
                                                    :label="$t('lieferanten.rechnungformular.einzelpreis') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @focus="onCurrencyFocus(`pos_${getPositionId(position)}_unitPrice`)"
                                                    @blur="onCurrencyBlur(handleBlur)"
                                                    @update:model-value="
                                                        handleCurrencyInput(`pos_${getPositionId(position)}_unitPrice`, $event, position, 'unitPrice');
                                                        calculatePositionTotals(index);
                                                    "
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.vatRate"
                                                :label="$t('lieferanten.rechnungformular.mwstSatz')"
                                                :name="`positions[${getPositionId(position)}].vatRate`"
                                            >
                                                <v-select
                                                    v-model="position.vatRate"
                                                    :items="mwstSatzItems"
                                                    :label="$t('lieferanten.rechnungformular.mwstSatz') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :error-messages="errors"
                                                    @blur="handleBlur"
                                                    @update:model-value="calculatePositionTotals(index)"
                                                ></v-select>
                                            </Field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.taxableAmount"
                                                :name="`positions[${getPositionId(position)}].taxableAmount`"
                                                :label="$t('lieferanten.rechnungformular.totalExklMwst')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.totalExclVat')"
                                            >
                                                <v-text-field
                                                    :model-value="
                                                        getCurrencyDisplayValue(`pos_${getPositionId(position)}_taxableAmount`, position.taxableAmount)
                                                    "
                                                    :label="$t('lieferanten.rechnungformular.totalExklMwst') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :hint="$t('lieferanten.rechnungformular.berechnetHint')"
                                                    persistent-hint
                                                    :error-messages="errors"
                                                    @focus="onCurrencyFocus(`pos_${getPositionId(position)}_taxableAmount`)"
                                                    @blur="onCurrencyBlur(handleBlur)"
                                                    @update:model-value="
                                                        handleCurrencyInput(`pos_${getPositionId(position)}_taxableAmount`, $event, position, 'taxableAmount')
                                                    "
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <Field
                                                v-slot="{ errors, handleBlur }"
                                                v-model="position.taxAmountPayable"
                                                :name="`positions[${getPositionId(position)}].taxAmountPayable`"
                                                :label="$t('lieferanten.rechnungformular.totalMwst')"
                                                :rules="getValidation('lieferantRechnungFormularPositionApiDto.totalVat')"
                                            >
                                                <v-text-field
                                                    :model-value="
                                                        getCurrencyDisplayValue(`pos_${getPositionId(position)}_taxAmountPayable`, position.taxAmountPayable)
                                                    "
                                                    :label="$t('lieferanten.rechnungformular.totalMwst') + ' *'"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    :hint="$t('lieferanten.rechnungformular.berechnetHint')"
                                                    persistent-hint
                                                    :error-messages="errors"
                                                    @focus="onCurrencyFocus(`pos_${getPositionId(position)}_taxAmountPayable`)"
                                                    @blur="onCurrencyBlur(handleBlur)"
                                                    @update:model-value="
                                                        handleCurrencyInput(
                                                            `pos_${getPositionId(position)}_taxAmountPayable`,
                                                            $event,
                                                            position,
                                                            'taxAmountPayable'
                                                        )
                                                    "
                                                ></v-text-field>
                                            </Field>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                        <v-btn v-if="positions.length > 0" color="primary" class="mb-4 mt-4" @click="addPosition(true)">
                            <v-icon left>mdi-plus</v-icon>
                            {{ $t('lieferanten.rechnungformular.positionHinzufuegen') }}
                        </v-btn>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Summary Card -->
            <v-card class="mt-6">
                <v-card-text>
                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <Field v-slot="{ errors, handleBlur }" v-model="formData.roundingAmount" name="summaryRoundingAmount" rules="required">
                                <v-select
                                    v-model="formData.roundingAmount"
                                    :items="roundingOptions"
                                    :label="$t('lieferanten.rechnungformular.rundungsbetrag') + ' *'"
                                    variant="outlined"
                                    density="comfortable"
                                    :error-messages="errors"
                                    @blur="handleBlur"
                                />
                            </Field>
                        </v-col>
                        <v-col cols="12" md="6"></v-col>
                    </v-row>
                    <v-divider class="mb-4"></v-divider>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-alert type="info" density="compact" variant="tonal" class="text-caption mb-2">
                                {{ $t('lieferanten.rechnungsformular.daten.uebereinstimmung') }}
                            </v-alert>
                            <v-card variant="outlined" density="compact" class="pa-3">
                                <div class="d-flex flex-column">
                                    <span class="text-subtitle-2 mb-1 d-flex">
                                        <strong class="text-right" style="min-width: 180px">{{ $t('lieferanten.rechnungformular.tvskundennummer') }}:</strong>
                                        <span class="ml-2">{{ formData.tvsCustomerNumber }}</span>
                                    </span>
                                    <span class="text-subtitle-2 mb-1 d-flex">
                                        <strong class="text-right" style="min-width: 180px">{{ $t('lieferanten.rechnungformular.firmenname') }}:</strong>
                                        <span class="ml-2">{{ formData.companyName }}</span>
                                    </span>
                                    <span class="text-subtitle-2 mb-1 d-flex">
                                        <strong class="text-right" style="min-width: 180px">{{ $t('lieferanten.rechnungformular.ort') }}:</strong>
                                        <span class="ml-2">{{ formData.zipCode }} {{ formData.cityName }}</span>
                                    </span>
                                </div>

                                <v-divider v-if="positions.length > 0" class="my-4"></v-divider>

                                <v-simple-table v-if="positions.length > 0" density="compact" class="vat-summary-table">
                                    <template #default>
                                        <thead>
                                            <tr>
                                                <th class="text-left text-caption font-weight-bold pa-1"></th>
                                                <th class="text-right text-caption font-weight-bold pa-1">
                                                    {{ $t('lieferanten.rechnungformular.totalExklMwst') }}
                                                </th>
                                                <th class="text-right text-caption font-weight-bold pa-1">
                                                    {{ $t('lieferanten.rechnungformular.mwstSatz') }}
                                                </th>
                                                <th class="text-right text-caption font-weight-bold pa-1">
                                                    {{ $t('lieferanten.rechnungformular.totalMwst') }}
                                                </th>
                                                <th class="text-right text-caption font-weight-bold pa-1">
                                                    {{ $t('lieferanten.rechnungformular.totalInklMwst') }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="group in vatGroups" :key="group.rate">
                                                <td class="text-left text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1">{{ formatSwissCurrency(group.net) }}</td>
                                                <td class="text-right text-caption pa-1">{{ group.rate }}%</td>
                                                <td class="text-right text-caption pa-1">{{ formatSwissCurrency(group.vat) }}</td>
                                                <td class="text-right text-caption pa-1">{{ formatSwissCurrency(group.total) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-left text-caption font-weight-bold pa-1">{{ $t('lieferanten.rechnungformular.total') }}</td>
                                                <td class="text-right text-caption font-weight-bold pa-1">
                                                    {{ formatSwissCurrency(vatTotals.net) }}
                                                </td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption font-weight-bold pa-1">
                                                    {{ formatSwissCurrency(vatTotals.vat) }}
                                                </td>
                                                <td class="text-right text-caption font-weight-bold pa-1">
                                                    {{ formatSwissCurrency(vatTotals.total) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-left text-caption pa-1">{{ $t('lieferanten.rechnungformular.rundungsdifferenz') }}</td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1">
                                                    {{ formatSwissCurrency(roundingDifference) }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="text-left text-subtitle-2 font-weight-bold pa-1">
                                                    {{ $t('lieferanten.rechnungformular.endbetrag') }}
                                                </td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-caption pa-1"></td>
                                                <td class="text-right text-subtitle-2 font-weight-bold pa-1">
                                                    {{ formatSwissCurrency(totalInclVat) }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-center justify-center">
                            <ConfirmDialog
                                :title="$t('lieferanten.rechnungformular.bestaetigung')"
                                :text="$t('lieferanten.rechnungformular.bestaetigungText')"
                                @confirm="submitForm"
                            >
                                <template #activatorElement="{ props }">
                                    <v-btn
                                        id="submit-btn"
                                        v-bind="props"
                                        color="primary"
                                        size="large"
                                        :disabled="!meta.valid || positions.length === 0 || missingDataAlert || !pdfFile"
                                        :loading="isSubmitting"
                                        @click="handleSubmit(validate, $event)"
                                    >
                                        {{ $t('lieferanten.rechnungsformular.einreichen') }}
                                    </v-btn>
                                </template>
                            </ConfirmDialog>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </Form>

        <CustomerPickerDialog v-model:visible="showCustomerPicker" @select="onCustomerSelected" />
    </v-container>
</template>

<script lang="ts">
import { ScreenDetailed, ScreenDetails } from '@/types/ScreenDetails';
import { Component, mixins, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import { Field, Form } from 'vee-validate';
import { DateMixin } from '@/mixins/DateMixin';
import { CurrencyMixin } from '@/mixins/CurrencyMixin';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_RESOURCE } from '@/store/modules/resource';
import { NAME as NAME_VALIDATION } from '@/store/modules/validation';
import { NAME as NAME_LIEFERANT } from '@/areas/lieferant/store';

import { GET_MWST_SATZ_LIST, GET_TVS_KUNDE_DETAILS, GET_UNITS_OF_MEASUREMENT, GETLIEFERANTDETAILDATA } from '@/areas/lieferant/store/getters';
import {
    CLEAR_TVS_KUNDE_DETAILS,
    LOAD_LIEFERANT_DETAILS,
    LOAD_MWST_SATZ_LIST,
    LOAD_TVS_KUNDE_DETAILS,
    LOAD_UNITS_OF_MEASUREMENT,
    SUBMIT_INVOICE
} from '@/areas/lieferant/store/actions';
import { BenutzerInfoApiDto, LieferantStammdatenApiDto, SelectListItemApiDto } from '@/api-schema';
import { GET_LANGUAGE } from '@/store/modules/resource/getters';
import { GET_VALIDATION_KEY } from '@/store/modules/validation/getters';
import { BENUTZERINFO, HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';
import { FOOTERTITLE, SUBTITLE } from '@/store/modules/general/mutations';

import { markRaw } from 'vue';
import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TableHeader from '@/components/TableHeader.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import NowhowDatepicker from '@/components/NowhowDatepicker.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import CustomerPickerDialog from '../components/CustomerPickerDialog.vue';
import { onBeforeRouteLeave } from 'vue-router';
import { InvoiceFormData, InvoicePosition, SupplierInformation, VetdataInvoiceData } from '@/types/LieferantRechnungFormularDtos';

const lieferantModul = namespace(NAME_LIEFERANT);
const generalModul = namespace(NAME_GENERAL);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const resourceModul = namespace(NAME_RESOURCE);
const validationModul = namespace(NAME_VALIDATION);

type FormData = InvoiceFormData;
type Position = InvoicePosition;

const popupRefs = {
    pdfPreviewWindow: null as Window | null
};

@Component({
    components: {
        Form,
        Field,
        SingleFilter,
        FilterDrawer,
        TableHeader,
        ConfirmDialog,
        NowhowDatepicker,
        TextHighlights,
        CustomerPickerDialog
    }
})
export default class LieferantRechnungFormular extends mixins(DateMixin, CurrencyMixin) {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Getter(BENUTZERINFO) benutzerInfo!: BenutzerInfoApiDto;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;

    @resourceModul.Getter(GET_LANGUAGE) language!: string;
    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    @lieferantModul.Action(SUBMIT_INVOICE) submitInvoice!: Function;
    @lieferantModul.Getter(GETLIEFERANTDETAILDATA) lieferantStammdaten!: LieferantStammdatenApiDto;
    @lieferantModul.Getter(GET_MWST_SATZ_LIST) getMwstSatzList!: SelectListItemApiDto[];
    @lieferantModul.Getter(GET_UNITS_OF_MEASUREMENT) unitsOfMeasurement!: SelectListItemApiDto[];
    @lieferantModul.Getter(GET_TVS_KUNDE_DETAILS) tvsKundeDetails!: any;

    @lieferantModul.Action(LOAD_LIEFERANT_DETAILS) loadLieferantDetails!: Function;
    @lieferantModul.Action(LOAD_UNITS_OF_MEASUREMENT) loadUnitsOfMeasurement!: Function;
    @lieferantModul.Action(LOAD_TVS_KUNDE_DETAILS) loadTvsKundeDetails!: Function;
    @lieferantModul.Action(CLEAR_TVS_KUNDE_DETAILS) clearTvsKundeDetails!: Function;
    @lieferantModul.Action(LOAD_MWST_SATZ_LIST) loadMwstSatzList!: Function;

    @validationModul.Getter(GET_VALIDATION_KEY) getValidation!: (key: string) => string;

    /**
     * Zeigt eine Warnung an, wenn die Stammdaten des Lieferanten unvollständig sind.
     * Wird auf true gesetzt, wenn erforderliche Felder (MwSt-Nummer, Firma, Adresse, PLZ, Ort, Land) fehlen.
     */
    missingDataAlert = false;

    declare $refs: {
        formRef: InstanceType<typeof Form>;
    };

    date = '';
    activePanel = 'header';
    expandedPositions: string[] = [];
    hasUnsavedChanges = false;
    showConfirmDialog = false;
    isSubmitting = false;
    showCustomerPicker = false;
    focusedField: string | null = null;

    // Section validation tracking
    headerSectionTouched = false;
    headerSectionValid = false;
    customerSectionTouched = false;
    customerSectionValid = false;

    get dokumentTypes(): Array<{ title: string; value: string }> {
        return [
            {
                title: this.$t('lieferanten.rechnungformular.dokumenttyp.rechnung') as string,
                value: '380'
            },
            {
                title: this.$t('lieferanten.rechnungformular.dokumenttyp.gutschrift') as string,
                value: '381'
            }
        ];
    }

    formData: FormData = this.initFormData();

    pdfFile: File | null = null;
    pdfPreviewUrl: string | null = null;

    private initFormData(): FormData {
        const data = new InvoiceFormData();
        return data;
    }

    roundingOptions = [
        { title: '+0.02', value: 0.02 },
        { title: '+0.01', value: 0.01 },
        { title: '0.00', value: 0 },
        { title: '-0.01', value: -0.01 },
        { title: '-0.02', value: -0.02 }
    ];
    positions: Position[] = [];
    private positionIds = new Map<Position, string>();

    // Computed properties for totals
    get taxableAmount(): number {
        const positionsTotal = this.positions.reduce((sum, pos) => {
            return sum + (Number(pos.taxableAmount) || 0);
        }, 0);
        return Number(positionsTotal.toFixed(2));
    }

    get totalInclVat(): number {
        const positionsTotal = this.positions.reduce((sum, pos) => {
            const exclVat = Number(pos.taxableAmount) || 0;
            const vat = Number(pos.taxAmountPayable) || 0;
            return sum + exclVat + vat;
        }, 0);
        return Number((positionsTotal + (this.formData.roundingAmount || 0)).toFixed(2));
    }

    get vatGroups(): any[] {
        const groups: Record<number, { net: number; vat: number; total: number }> = {};

        this.positions.forEach((pos) => {
            const rate = Number(pos.vatRate) || 0;
            const net = Number(pos.taxableAmount) || 0;
            const vat = Number(pos.taxAmountPayable) || 0;

            if (!groups[rate]) {
                groups[rate] = { net: 0, vat: 0, total: 0 };
            }

            groups[rate].net += net;
            groups[rate].vat += vat;
            groups[rate].total += net + vat;
        });

        return Object.keys(groups)
            .map((rate) => ({
                rate: Number(rate),
                net: Number(groups[Number(rate)].net.toFixed(2)),
                vat: Number(groups[Number(rate)].vat.toFixed(2)),
                total: Number(groups[Number(rate)].total.toFixed(2))
            }))
            .sort((a, b) => a.rate - b.rate);
    }

    get vatTotals(): { net: number; vat: number; total: number } {
        return this.vatGroups.reduce(
            (acc, group) => {
                acc.net += group.net;
                acc.vat += group.vat;
                acc.total += group.total;
                return acc;
            },
            { net: 0, vat: 0, total: 0 }
        );
    }

    get roundingDifference(): number {
        return Number((this.totalInclVat - this.vatTotals.total).toFixed(2));
    }

    created() {
        this.setSubTitle(this.$t('lieferantrechnung.formular.title'));
        this.setFooterTitle('S540');
    }

    async mounted() {
        if (!this.hasAccess('Navigation.LieferantDokumentArchiv')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
        }

        window.addEventListener('beforeunload', this.handleBeforeUnload);

        onBeforeRouteLeave(() => {
            if (this.hasUnsavedChanges) {
                const answer = window.confirm(this.$t('lieferanten.rechnungformular.ungespeicherteAenderungen') as string);
                return answer;
            }
            return true;
        });
        await this.loadUnitsOfMeasurement();
        await this.loadMwstSatzList();
        this.addPosition(false);
        this.hasUnsavedChanges = false;
        this.checkLieferantDetails();
    }

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.revokePdfPreviewUrl();
        popupRefs.pdfPreviewWindow = null;
    }

    handleBeforeUnload(event: BeforeUnloadEvent) {
        if (this.hasUnsavedChanges) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    private checkLieferantDetails() {
        this.loadLieferantDetails(this.benutzerInfo.tvsLieferantenNummer).then(() => {
            this.missingDataAlert = !this.validateLieferantData();
        });
    }

    private validateLieferantData(): boolean {
        const data = this.lieferantStammdaten;
        if (!data) {
            return false;
        }
        const requiredFields = [data.mwstNummer, data.firma, data.adresszeile1, data.plz, data.ort, data.land];
        return requiredFields.every((field) => field && (typeof field === 'number' || field.trim() !== ''));
    }

    goToNext(panel: string) {
        // Mark current section as touched when navigating away
        this.activePanel = panel;
    }

    addPosition(focusAfterAdd = false) {
        const defaultVatRate = Number.parseFloat(this.getMwstSatzList.find((x) => x.selected)?.value ?? '8.1');

        const defaultUnitValue = this.unitsOfMeasurement.find((unit) => unit.selected)?.value ?? 'C62';

        const newPosition: Position = {
            SupplierArticleNumber: '',
            VetpointArticleNumber: '',
            gtin: '',
            productName: '',
            quantity: null,
            unitOfMeasurement: defaultUnitValue,
            unitPrice: null,
            vatRate: defaultVatRate,
            taxableAmount: null,
            taxAmountPayable: null
        };

        this.positions.push(newPosition);
        // Generate a unique ID for this position
        this.positionIds.set(newPosition, `position-${Date.now()}-${Math.random()}`);
        this.expandedPositions.push(this.getPositionId(newPosition));

        this.hasUnsavedChanges = true;

        if (focusAfterAdd) {
            this.focusPositionInput(this.positions.length - 1);
        }
    }
    removePosition(index: number) {
        const removedPosition = this.positions[index];
        const removedId = this.getPositionId(removedPosition);
        this.positionIds.delete(removedPosition);
        this.positions.splice(index, 1);
        this.expandedPositions = this.expandedPositions.filter((id) => id !== removedId);
        this.hasUnsavedChanges = true;
    }

    getPositionId(position: Position): string {
        if (!this.positionIds.has(position)) {
            this.positionIds.set(position, `position-${Date.now()}-${Math.random()}`);
        }
        return this.positionIds.get(position)!;
    }

    focusPositionInput(index: number, maxAttempts = 20) {
        let attempts = 0;

        const tryFocus = () => {
            const positionPanels = document.querySelectorAll('[data-section="positions"] .v-expansion-panels .v-expansion-panel');
            const targetPanel = positionPanels[index];
            const firstInput = targetPanel?.querySelector('.v-expansion-panel-text input') as HTMLElement;

            if (firstInput && firstInput.offsetParent !== null) {
                firstInput.focus();
            } else if (attempts < maxAttempts) {
                attempts++;
                requestAnimationFrame(tryFocus);
            }
        };

        requestAnimationFrame(tryFocus);
    }

    calculatePositionTotals(index: number) {
        const position = this.positions[index];
        if (position.quantity != null && position.unitPrice != null) {
            const totalExcl = position.quantity * position.unitPrice;
            position.taxableAmount = Number(totalExcl.toFixed(2));
            position.taxAmountPayable = Number((totalExcl * (position.vatRate / 100)).toFixed(2));
        }
    }

    handleTabFromLastField(event: KeyboardEvent, currentSection: string) {
        if (!event.shiftKey) {
            event.preventDefault();
            const nextSection = currentSection === 'header' ? 'customer' : 'positions';
            this.goToNext(nextSection);
            this.focusFirstInputInSection(nextSection);
        }
    }

    handleTabToPositions(event: KeyboardEvent) {
        if (!event.shiftKey) {
            event.preventDefault();
            this.goToNext('positions');

            // Focus the SupplierArticleNumber input in the first position
            if (this.positions.length > 0) {
                this.focusPositionInput(0);
            }
        }
    }

    focusFirstInputInSection(section: string, maxAttempts = 20) {
        let attempts = 0;

        const tryFocus = () => {
            const panelContent = document.querySelector(`[data-section="${section}"] .v-expansion-panel-text`);
            const element = panelContent?.querySelector('input, textarea, button') as HTMLElement;

            // Check if input exists AND is visible (offsetParent !== null)
            if (element && element.offsetParent !== null) {
                element.focus();
                console.log('Focused after', attempts, 'attempts');
            } else if (attempts < maxAttempts) {
                attempts++;
                requestAnimationFrame(tryFocus);
            } else {
                console.log('Failed to focus after', maxAttempts, 'attempts');
            }
        };

        requestAnimationFrame(tryFocus);
    }

    goToLieferanteinstellungen() {
        this.$router.push({ name: 'LieferantEinstellungen' });
    }

    async handleSubmit(validate: () => Promise<{ valid: boolean }>) {
        const { valid } = await validate();

        if (!valid) {
            this.alertMessage({
                text: this.$t('lieferanten.rechnungformular.validierungsfehler'),
                color: 'error'
            });
            return;
        }

        if (this.positions.length === 0) {
            this.alertMessage({
                text: this.$t('lieferanten.rechnungformular.mindestensEinePosition'),
                color: 'warning'
            });
            return;
        }

        if (!this.pdfFile) {
            this.alertMessage({
                text: this.$t('lieferanten.rechnungformular.pdfErforderlich'),
                color: 'warning'
            });
            return;
        }

        this.showConfirmDialog = true;
    }

    async submitForm() {
        try {
            this.showConfirmDialog = false;
            this.isSubmitting = true;

            const success = await this.submitInvoice({
                invoiceData: this.buildInvoiceData(),
                pdfFile: this.pdfFile,
                formData: this.formData
            });

            if (success) {
                const win = popupRefs.pdfPreviewWindow;
                popupRefs.pdfPreviewWindow = null;
                if (win) {
                    try {
                        if (!win.closed) {
                            win.close();
                        }
                    } catch {
                        // cross-origin or already closed
                    }
                }
                this.hasUnsavedChanges = false;
                await this.$router.push({ path: '/redirect' });
                await this.$router.replace({ name: 'LieferantRechnungFormular' });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isSubmitting = false;
        }
    }

    get unitOfMeasurementItems() {
        return this.unitsOfMeasurement.map((unit) => ({
            title: unit.text,
            value: unit.value
        }));
    }

    get mwstSatzItems() {
        return this.getMwstSatzList.map((vat) => {
            // von String zu Nummer parsen
            const n = Number.parseFloat(vat.value); // "8.100" -> 8.1
            return {
                title: `${n}`,
                value: n
            };
        });
    }

    getCurrencyDisplayValue(fieldName: string, value: number | null | undefined): string {
        if (this.focusedField === fieldName) {
            return value != null ? value.toString() : '';
        }
        return this.formatSwissCurrency(value);
    }

    handleCurrencyInput(fieldName: string, value: string, target: any, targetField: string) {
        const parsed = this.parseSwissCurrency(value);
        target[targetField] = parsed;
        this.hasUnsavedChanges = true;
    }

    onCurrencyFocus(fieldName: string) {
        this.focusedField = fieldName;
    }

    onCurrencyBlur(handleBlur: Function) {
        this.focusedField = null;
        handleBlur();
    }

    onCustomerSelected(customer: any) {
        this.formData.tvsCustomerNumber = String(customer.tvsKundennummer ?? '');
        this.showCustomerPicker = false;
        this.hasUnsavedChanges = true;
    }

    private buildInvoiceData(): VetdataInvoiceData {
        // Map LieferantDetailApiDto to SupplierInformation
        const supplierInfo: SupplierInformation = {
            tvsLieferantennummer: this.benutzerInfo.tvsLieferantenNummer,
            mwstNummer: this.lieferantStammdaten.mwstNummer || '',
            firma: this.lieferantStammdaten.firma || '',
            adresszeile1: this.lieferantStammdaten.adresszeile1 || '',
            adresszeile2: this.lieferantStammdaten.adresszeile2 || undefined,
            adresszeile3: this.lieferantStammdaten.adresszeile3 || undefined,
            plz: this.lieferantStammdaten.plz || 0,
            ort: this.lieferantStammdaten.ort || '',
            land: this.lieferantStammdaten.land || ''
        };

        // Build complete invoice data
        const invoiceData: VetdataInvoiceData = {
            formData: this.formData,
            positions: this.positions,
            supplierInfo: supplierInfo,
            totalTaxableAmount: this.taxableAmount,
            totalInclVat: this.totalInclVat,
            vatSummary: this.vatGroups.map((g) => {
                return {
                    vatRate: g.rate,
                    taxableAmount: g.net,
                    taxAmountPayable: g.vat
                };
            })
        };

        return invoiceData;
    }

    @Watch('activePanel')
    onActivePanelChanged(newVal: string, oldVal: string) {
        if (oldVal === 'header') {
            this.headerSectionTouched = true;
        } else if (oldVal === 'customer') {
            this.customerSectionTouched = true;
        }

        if (newVal === 'header') {
            this.validateHeaderSection();
        } else if (newVal === 'customer') {
            this.validateCustomerSection();
        }
    }

    @Watch('formData', { deep: true })
    onFormDataChanged() {
        this.hasUnsavedChanges = true;
        this.validateHeaderSection();
        this.validateCustomerSection();
    }

    @Watch('positions', { deep: true })
    onPositionsChanged() {
        this.hasUnsavedChanges = true;
    }

    @Watch('language')
    onLanguageChanged() {
        this.setSubTitle(this.$t('lieferantrechnung.formular.title'));
    }

    // automatisiertes Abfüllen von Werten

    async onSupplierArticleNumberChange(index: number) {
        const position = this.positions[index];

        if (!position.SupplierArticleNumber) {
            return;
        }

        // Vetpoint Artikelnummer direkt aus TvsLieferantId und Artikelnummer bauen
        const tvsLieferantId = this.benutzerInfo.tvsLieferantenNummer;
        this.positions[index].VetpointArticleNumber = `${tvsLieferantId}-${position.SupplierArticleNumber}`;
    }
    @Watch('formData.documentIssueDate')
    onDocumentIssueDateChanged(val: string) {
        if (val && !this.formData.deliveryDate) {
            this.formData.deliveryDate = val;
        }
    }

    @Watch('formData.tvsCustomerNumber')
    onKundenNummerChanged(val: string) {
        if (val && val.length === 4 && /^\d{4}$/.test(val)) {
            this.loadTvsKundeDetails(val)
                .then(() => {
                    console.log('TVS customer details loaded:', this.tvsKundeDetails);
                    // Fill form fields with the loaded TVS customer details
                    if (this.tvsKundeDetails) {
                        this.formData.companyName = this.tvsKundeDetails.firma || '';
                        this.formData.address1 = this.tvsKundeDetails.strasse + ' ' + this.tvsKundeDetails.hausNummer || '';
                        this.formData.address2 = '';
                        this.formData.address3 = '';
                        this.formData.zipCode = this.tvsKundeDetails.plz ? String(this.tvsKundeDetails.plz) : '';
                        this.formData.cityName = this.tvsKundeDetails.ort || '';
                        this.formData.country = this.tvsKundeDetails.landcode || '';
                    } else {
                        this.formData.tvsCustomerNumber = '';
                        this.alertMessage({
                            text: this.$t('lieferanten.rechnungformular.gueltigeNummerErforderlich'),
                            color: 'error'
                        });
                    }
                })
                .catch(() => {
                    this.formData.tvsCustomerNumber = '';
                    this.alertMessage({
                        text: this.$t('lieferanten.rechnungformular.gueltigeNummerErforderlich'),
                        color: 'error'
                    });
                });
        } else {
            this.clearTvsKundeDetails();
        }
    }

    onPdfFileChanged(file: File | null) {
        this.revokePdfPreviewUrl();

        if (file) {
            this.pdfPreviewUrl = URL.createObjectURL(file);
            this.hasUnsavedChanges = true;
            setTimeout(() => {
                this.openPdfPreview();
            }, 100);
        } else {
            this.pdfPreviewUrl = null;
        }
    }

    async openPdfPreview() {
        console.log('Opening PDF preview');
        if (!this.pdfPreviewUrl) {
            return;
        }

        let left, top, width, height;

        // Try the Window Management API (Chrome 100+, Edge 100+)
        if ('getScreenDetails' in window) {
            try {
                const screenDetails = await window.getScreenDetails();
                const screens = screenDetails.screens;
                const currentScreen = screenDetails.currentScreen;

                // Find a screen that isn't the current one
                const secondScreen = screens.find((s) => s !== currentScreen);

                if (secondScreen) {
                    // Full-size on the second monitor
                    left = secondScreen.availLeft;
                    top = secondScreen.availTop;
                    width = secondScreen.availWidth;
                    height = secondScreen.availHeight;
                } else {
                    // Single monitor — right 50%
                    ({ left, top, width, height } = this.getFallbackPosition());
                }
            } catch (e) {
                // Permission denied or error — fall back
                ({ left, top, width, height } = this.getFallbackPosition());
            }
        } else {
            // API not supported — fall back
            ({ left, top, width, height } = this.getFallbackPosition());
        }

        const win = window.open(
            this.pdfPreviewUrl,
            '_blank',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
        );

        // Store non-reactively in module scope
        popupRefs.pdfPreviewWindow = win ? markRaw(win) : null;
        console.log('Is raw?', popupRefs.pdfPreviewWindow && (popupRefs.pdfPreviewWindow as any).__v_skip);
    }

    getFallbackPosition() {
        const width = window.screen.width * 0.5;
        const height = window.screen.height;
        const currentScreenLeft = window.screenLeft ?? window.screenX ?? 0;
        const left = currentScreenLeft + (window.screen.width - width);
        const top = 0;
        return { left, top, width, height };
    }

    private revokePdfPreviewUrl() {
        if (this.pdfPreviewUrl) {
            URL.revokeObjectURL(this.pdfPreviewUrl);
            this.pdfPreviewUrl = null;
        }
    }

    validateHeaderSection() {
        const { documentType, documentIdentifier, documentIssueDate, customerNumber, deliveryDate } = this.formData;
        this.headerSectionValid = !!(documentType && documentIdentifier && documentIssueDate && customerNumber && deliveryDate);
    }

    validateCustomerSection() {
        const { tvsCustomerNumber, companyName, address1, zipCode, cityName, country } = this.formData;
        this.customerSectionValid = !!(tvsCustomerNumber && companyName && address1 && zipCode && cityName && country);
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}

.v-expansion-panel-title h3 {
    margin: 0;
}

.vat-summary-table :deep(table) {
    border-collapse: collapse;
}

.vat-summary-table :deep(th),
.vat-summary-table :deep(td) {
    border: none !important;
}
</style>
